// Hook partagé de l'abonnement réel (Bloc 7).
//
// Utilisé par l'espace Propriétaire ET l'espace Agence : la source de vérité est
// la RPC Supabase `abonnement_du_compte()`. Aucune duplication de logique.
//
// Deux notions distinctes sont exposées :
//   - `formuleId`         : la formule EFFECTIVE (droits et limites réellement
//                           appliqués côté serveur — « Gratuit » tant que le
//                           paiement n'est pas validé par le Super Admin).
//   - `formuleAfficheeId` : la formule à MONTRER (celle choisie par le client),
//                           afin que son choix à l'inscription soit conservé et
//                           affiché, même avant activation.
//   - `paiementRequis`    : une formule payante a été choisie mais n'est pas
//                           encore active (paiement requis / en attente).

import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormuleId, Periodicite } from "@/config/formules";
import { getFormuleConfig } from "@/config/formules";
import { useSession } from "@/session/SessionProvider";
import {
  abonnementStatutFromDb,
  choisirFormuleRpc,
  demandePaiementFromRpc,
  estEssaiActif,
  fetchMonAbonnement,
  formuleChoisieCodeFromRpc,
  formuleCodeFromRpc,
  joursRestantsEssai,
  periodiciteFromDb,
  renouvelerAbonnementRpc,
  type AbonnementUsage,
  type DemandePaiement,
  type MonAbonnement,
} from "./abonnement";
import { finaliserInscriptionSiBesoin } from "./inscriptionPaiement";

type AbonnementStatut = "actif" | "en_attente_paiement" | "brouillon" | "expire" | "resilie";

export interface MonAbonnementState {
  /** Formule effective (droits et limites appliqués côté serveur). */
  formuleId: FormuleId;
  /** Formule à afficher (choisie par le client, conservée avant activation). */
  formuleAfficheeId: FormuleId;
  /** Une formule payante est choisie mais n'est pas encore active. */
  paiementRequis: boolean;
  /** Offre de lancement : un essai gratuit (accès complet) est actuellement actif. */
  estEssai: boolean;
  /** Date de fin de l'essai gratuit (vide si aucun essai actif). */
  essaiDateFin: string;
  /** Jours restants avant la fin de l'essai (null si aucun essai actif). */
  joursRestantsEssai: number | null;
  /** Demande de paiement réelle (en attente / validée / refusée) ou null. */
  demandePaiement: DemandePaiement | null;
  periodicite: Periodicite;
  statut: AbonnementStatut;
  dateDebut: string;
  dateFin: string;
  /** Date limite de validation du paiement (vide si aucun paiement en attente). */
  dateLimitePaiement: string;
}

export interface UseMonAbonnementValue {
  abonnement: MonAbonnementState;
  /** L'abonnement autorise-t-il les opérations métier (création/modification) ? */
  actif: boolean;
  /** L'abonnement est-il expiré ? (lecture conservée, écritures bloquées côté serveur) */
  expire: boolean;
  usage: AbonnementUsage | null;
  chargement: boolean;
  erreur: string | null;
  setPeriodicite: (periodicite: Periodicite) => void;
  changerFormule: (id: FormuleId, periodicite: Periodicite) => Promise<void>;
  renouveler: () => Promise<void>;
  rafraichir: () => Promise<void>;
}

const DEFAULT_STATE: MonAbonnementState = {
  formuleId: "gratuit",
  formuleAfficheeId: "gratuit",
  paiementRequis: false,
  estEssai: false,
  essaiDateFin: "",
  joursRestantsEssai: null,
  demandePaiement: null,
  periodicite: "mensuel",
  statut: "actif",
  dateDebut: "",
  dateFin: "",
  dateLimitePaiement: "",
};

export function useMonAbonnement(): UseMonAbonnementValue {
  const { session } = useSession();
  const [data, setData] = useState<MonAbonnement | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [pendingPeriodicite, setPendingPeriodicite] = useState<Periodicite | null>(null);

  const rafraichir = useCallback(async () => {
    if (!session) {
      setData(null);
      return;
    }
    try {
      setChargement(true);
      const result = await fetchMonAbonnement();
      setData(result);
      setErreur(null);
    } catch {
      setErreur("La lecture de votre abonnement a échoué. Réessayez plus tard.");
    } finally {
      setChargement(false);
    }
  }, [session]);

  useEffect(() => {
    void (async () => {
      // Transfert automatique de la preuve d'inscription dès qu'une session
      // existe : le client ne re-fournit jamais sa preuve. Idempotent côté
      // serveur (verrou + index unique) : jamais de doublon.
      if (session) await finaliserInscriptionSiBesoin();
      await rafraichir();
    })();
  }, [rafraichir, session]);

  const abonnement = useMemo<MonAbonnementState>(() => {
    if (!data || !data.connecte) {
      return { ...DEFAULT_STATE, periodicite: pendingPeriodicite ?? DEFAULT_STATE.periodicite };
    }
    const code = formuleCodeFromRpc(data);
    const gratuit = code === "gratuit";
    const chosenId = formuleChoisieCodeFromRpc(data);
    const statutDb = data.abonnement?.statut ?? null;
    const estEssai = estEssaiActif(data);
    const finEssai = estEssai ? data.abonnement?.date_fin ?? "" : "";
    const joursEssai = estEssai ? joursRestantsEssai(data) : null;

    // Une formule payante reste « en attente » tant que l'abonnement n'est pas
    // validé par le Super Admin :
    //   - soit aucune ligne d'abonnement n'existe encore (inscription non payée),
    //   - soit l'abonnement est au statut « en_attente » (paiement soumis, non validé).
    // Pendant un essai gratuit actif, AUCUN paiement n'est requis.
    const choisiePayante = chosenId !== null && (getFormuleConfig(chosenId)?.prixMensuel ?? 0) > 0;
    const paiementRequis = !estEssai && gratuit && (statutDb === "en_attente" || choisiePayante);

    let statut: AbonnementStatut;
    if (estEssai) statut = "actif";
    else if (paiementRequis) statut = "en_attente_paiement";
    else if (!data.abonnement) statut = "actif";
    else statut = abonnementStatutFromDb(statutDb, data.expire);

    // La formule « gratuite » n'a ni période ni renouvellement, mais l'essai, lui,
    // possède de vraies dates de début/fin : on les conserve dans ce cas.
    const sansPeriode = gratuit && !estEssai;

    return {
      formuleId: code,
      formuleAfficheeId: paiementRequis ? (chosenId ?? code) : code,
      paiementRequis,
      estEssai,
      essaiDateFin: finEssai,
      joursRestantsEssai: joursEssai,
      demandePaiement: demandePaiementFromRpc(data),
      periodicite: pendingPeriodicite ?? periodiciteFromDb(data.abonnement?.periodicite),
      statut,
      dateDebut: sansPeriode ? "" : data.abonnement?.date_debut ?? "",
      dateFin: sansPeriode ? "" : data.abonnement?.date_fin ?? "",
      dateLimitePaiement: paiementRequis ? data.date_limite_paiement ?? "" : "",
    };
  }, [data, pendingPeriodicite]);

  const setPeriodicite = useCallback((periodicite: Periodicite) => {
    setPendingPeriodicite(periodicite);
  }, []);

  const changerFormule = useCallback(async (id: FormuleId, periodicite: Periodicite) => {
    try {
      setErreur(null);
      const result = await choisirFormuleRpc(id, periodicite);
      setData(result);
      setPendingPeriodicite(null);
    } catch {
      setErreur("Le changement de formule a échoué. Réessayez.");
    }
  }, []);

  const renouveler = useCallback(async () => {
    try {
      setErreur(null);
      const result = await renouvelerAbonnementRpc();
      setData(result);
    } catch {
      setErreur("Le renouvellement a échoué. Réessayez.");
    }
  }, []);

  return {
    abonnement,
    actif: data?.actif ?? true,
    expire: data?.expire ?? false,
    usage: data?.usage ?? null,
    chargement,
    erreur,
    setPeriodicite,
    changerFormule,
    renouveler,
    rafraichir,
  };
}